import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, LockIcon, CreditCardIcon, ZapIcon } from 'lucide-react';
import { PRICE, INCLUDES, BONUSES } from '../constants/content';
import { PriceDisplay } from './PriceDisplay';
import { CtaButton } from './CtaButton';
export function PricingOffer() {
  return <section id="oferta" className="w-full bg-white py-14 sm:py-20" data-id="element-134">
      <div className="mx-auto max-w-3xl px-4" data-id="element-135">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mb-8 text-center" data-id="element-136">
          <h2 className="font-display text-3xl font-700 leading-tight text-ink sm:text-4xl" data-id="element-137">
            Entonces Franco, ¿Cuánto Cuesta Esto?
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-gray-700" data-id="element-138">
            Si sumás lo que cuesta un contador por mes, lo que perdés en cajones
            mal calculados y lo que te clavan los proveedores por no saber
            comparar precios, esto se paga solo la primera semana.
          </p>
        </motion.div>

        {/* Offer card */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.97
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="relative overflow-hidden rounded-3xl border-4 border-crimson bg-cream shadow-2xl" data-id="element-139">
          {/* Discount badge */}
          <div className="absolute -right-12 top-6 z-10 rotate-45 bg-crimson px-14 py-1.5 text-center text-sm font-700 uppercase tracking-wide text-white shadow-md" data-id="element-140">
            -70% OFF
          </div>

          <div className="grid gap-6 p-6 sm:p-8 md:grid-cols-2 md:items-center" data-id="element-141">
            {/* Mockup */}
            <div className="order-2 md:order-1" data-id="element-142">
              <picture data-id="element-143">
                <source type="image/webp" srcSet="https://cdn.magicpatterns.com/uploads/n8U3rLJSdAsr1BoTB3NEJY/14-400.webp 400w, https://cdn.magicpatterns.com/uploads/qggzdpAhMKotXbbzTL4uzb/14-700.webp 700w" sizes="(min-width: 768px) 320px, 100vw" data-id="element-144" />
                <source type="image/jpeg" srcSet="https://cdn.magicpatterns.com/uploads/t7gmQQM7F3y7mDWLBPsN6A/14-400.jpg 400w, https://cdn.magicpatterns.com/uploads/gqmmS528H6RV4F2nirbs4h/14-700.jpg 700w" sizes="(min-width: 768px) 320px, 100vw" data-id="element-145" />
                <img src="https://cdn.magicpatterns.com/uploads/gqmmS528H6RV4F2nirbs4h/14-700.jpg" alt="Cajón de pollo fresco del sistema Pollería Rentable 2026" width={800} height={800} loading="lazy" decoding="async" className="mx-auto w-full max-w-xs rounded-2xl object-cover shadow-lg ring-1 ring-black/5" data-id="element-146" />
              </picture>
            </div>

            {/* Price */}
            <div className="order-1 text-center md:order-2 md:text-left" data-id="element-147">
              <p className="mb-1 text-sm font-600 uppercase tracking-widest text-crimson" data-id="element-148">
                Precio de hoy
              </p>

              <div className="mb-2 flex items-center justify-center gap-3 md:justify-start" data-id="element-149">
                <PriceDisplay amount={PRICE.totalValue} strike className="text-xl font-500 text-gray-400" data-id="element-150" />
              </div>

              <PriceDisplay amount={PRICE.today} className="block font-display text-6xl font-700 leading-none text-crimson sm:text-7xl" symbolClassName="mr-1 align-top text-4xl" data-id="element-151" />
              <p className="mt-2 text-sm text-gray-600" data-id="element-152">
                Valor total de{' '}
                <PriceDisplay amount={PRICE.totalValue} className="font-600" data-id="element-153" />{' '}
                por menos de un tercio.
              </p>
            </div>
          </div>

          <div className="border-t border-black/10 p-6 sm:p-8" data-id="element-154">
            <CtaButton className="mb-4" data-id="element-155">COMPRAR AHORA</CtaButton>

            <a href="#" className="mb-5 block text-center text-sm font-600 text-crimson underline-offset-4 hover:underline" data-id="element-156">
              Montá Tu Pollería Rentable Hoy Mismo »
            </a>

            {/* Trust / payment */}
            <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs font-600 text-gray-500" data-id="element-157">
              <span className="inline-flex items-center gap-1.5" data-id="element-158">
                <LockIcon className="h-4 w-4 text-green-600" aria-hidden="true" data-id="element-159" />
                Pago 100% seguro
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-160">
                <ShieldCheckIcon className="h-4 w-4 text-green-600" aria-hidden="true" data-id="element-161" />
                Garantía de 30 días
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-162">
                <CreditCardIcon className="h-4 w-4 text-green-600" aria-hidden="true" data-id="element-163" />
                Mercado Pago · Tarjetas
              </span>
              <span className="inline-flex items-center gap-1.5" data-id="element-164">
                <ZapIcon className="h-4 w-4 text-gold-dark" aria-hidden="true" data-id="element-165" />
                Acceso instantáneo
              </span>
            </div>

            <p className="mt-4 text-center text-xs text-gray-500" data-id="element-166">
              Esto es un pago único. No hay cuotas mensuales.
            </p>
          </div>
        </motion.div>

        {/* Recap list */}
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mt-10 rounded-2xl bg-cream p-6 ring-1 ring-black/5" data-id="element-167">
          <h3 className="mb-4 text-center font-display text-2xl font-600 text-ink" data-id="element-168">
            Una Vez Más, Esto Es Todo Lo Que Vas A Recibir:
          </h3>

          {/* Value-stack visual */}
          <div className="mb-6 overflow-hidden rounded-2xl ring-1 ring-black/10" data-id="element-169">
            <img src="https://cdn.magicpatterns.com/uploads/bBaV3yKLYtqXrHYmxPddTK/A_dramatic_high-converting_dig_Nano_Banana_2_89850.png" alt="Pollería Rentable 2026 — de $432.990 a solo $29.990, con todos los bonos incluidos" width={2560} height={1440} loading="lazy" decoding="async" className="w-full object-contain" data-id="element-170" />
          </div>
          <ul className="space-y-2 text-sm text-gray-800" data-id="element-171">
            {INCLUDES.map((item, i) => <li key={i} className="flex items-start gap-2" data-id="element-172">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-crimson" data-id="element-173" />
                <span data-id="element-174">
                  <strong className="font-700" data-id="element-175">{item.title}</strong> —{' '}
                  {item.desc}
                  {item.value != null && <span className="ml-1 font-700 text-gold-dark" data-id="element-176">
                      (<PriceDisplay amount={item.value} data-id="element-177" /> de Valor)
                    </span>}
                </span>
              </li>)}
            {BONUSES.map(b => <li key={b.n} className="flex items-start gap-2" data-id="element-178">
                <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-gold" data-id="element-179" />
                <span data-id="element-180">
                  <strong className="font-700" data-id="element-181">
                    Bono #{b.n}: {b.title}
                  </strong>{' '}
                  <span className="font-700 text-gold-dark" data-id="element-182">
                    (<PriceDisplay amount={b.value} data-id="element-183" /> de Valor)
                  </span>
                </span>
              </li>)}
            <li className="flex items-start gap-2" data-id="element-184">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-green-600" data-id="element-185" />
              <span data-id="element-186">Garantía de 30 Días «Ámalo o Dejalo» sin riesgo</span>
            </li>
          </ul>

          <div className="mt-6 rounded-xl border-2 border-dashed border-crimson bg-white p-4 text-center" data-id="element-187">
            <p className="text-sm font-600 uppercase tracking-wide text-gray-500" data-id="element-188">
              Valor Total: <PriceDisplay amount={PRICE.totalValue} strike data-id="element-189" />
            </p>
            <p className="mt-1 text-base font-700 text-ink" data-id="element-190">
              Conseguilo Hoy Por Un Pago Único De:
            </p>
            <PriceDisplay amount={PRICE.today} className="font-display text-5xl font-700 text-crimson" symbolClassName="mr-0.5 align-top text-2xl" data-id="element-191" />
          </div>

          <div className="mt-6" data-id="element-192">
            <CtaButton data-id="element-193">COMPRAR AHORA</CtaButton>
          </div>
          <p className="mt-3 text-center text-xs text-gray-500" data-id="element-194">
            Esto es un pago único. No hay cuotas mensuales ni costos ocultos. Me
            reservo el derecho de terminar este descuento en cualquier momento
            sin previo aviso después del 9 de agosto de 2026.
          </p>
        </motion.div>
      </div>
    </section>;
}